import type { SuretyAccount } from "@portal/api/account";
import {
	BondRequest,
	useAcceptBondRequestTerms,
	useCloseBondRequest,
} from "@portal/api/bond-request/agent";
import { ConfirmationModalClickWrapper } from "@portal/components/layout/ConfirmationModalClickWrapper";
import { LoadingModal } from "@portal/components/layout/LoadingModal";
import { Modal } from "@portal/components/layout/Modal";
import { Button, Card, Icon } from "@portal/components/primitives";
import { MoreActionsMenu } from "@portal/components/ui/MoreActionsMenu";
import { useState } from "react";
import { ClosedReasonModal, ClosingAction } from "../../../../shared/account/ClosedReasonModal";
import { BondRequestLayout } from "../../../../shared/request/BondRequestLayout";
import { BondRequestTermsAndConditions } from "../../../../shared/request/BondRequestTermsAndConditions";

type ApprovedBondRequestViewProps = {
	request: BondRequest;
	account: SuretyAccount;
};

export const ApprovedBondRequestView = ({ request, account }: ApprovedBondRequestViewProps) => {
	const [showClosedReasonModal, setShowClosedReasonModal] = useState(false);
	const [closingAction, setClosingAction] = useState<ClosingAction | null>(null);
	const { acceptBondRequestTerms, acceptBondRequestTermsIsLoading } = useAcceptBondRequestTerms(
		request.suretyAccountId,
		request.id,
	);

	const { closeBondRequest, closeBondRequestLoading, closeBondRequestError } = useCloseBondRequest(
		request.suretyAccountId,
		request.id,
	);

	return (
		<BondRequestLayout
			request={request}
			account={account}
			termsAndConditionsOpen={false}
			actionButtons={
				<div className="flex items-center flex-col space-y-[4px]">
					<ConfirmationModalClickWrapper
						className="items-center"
						message="Are you sure you want to accept the terms?"
						onConfirm={acceptBondRequestTerms}
						confirmTrackingId="bond-request-agent-accept-terms-confirm"
						cancelTrackingId="bond-request-agent-accept-terms-cancel"
					>
						<Button
							color="primary"
							variant="solid"
							className="w-full"
							trackingId="bond-request-agent-accept-terms"
							leadingIcon={<Icon type="check" size="mini" />}
						>
							Accept Terms
						</Button>
					</ConfirmationModalClickWrapper>
					<MoreActionsMenu
						items={[
							{
								label: "Close Bond Request",
								icon: <Icon type="minus-circle" size="mini" />,
								onClick: () => {
									setShowClosedReasonModal(true);
									setClosingAction("close");
								},
								trackingId: "bond-request-agent-close",
							},
							{
								label: "Reject Terms",
								icon: <Icon type="x-mark" size="mini" />,
								onClick: () => {
									setShowClosedReasonModal(true);
									setClosingAction("reject");
								},
								trackingId: "bond-request-agent-reject-terms",
							},
						]}
						panelClassName="mt-[10px]"
					/>
				</div>
			}
		>
			<Card elevation="raised" className="rounded-xl" data-testid="agent-bond-request-terms-card">
				<Card.Body className="p-6">
					<BondRequestTermsAndConditions request={request} variant="card" />
				</Card.Body>
			</Card>
			{(acceptBondRequestTermsIsLoading || closeBondRequestLoading) && <LoadingModal />}
			{showClosedReasonModal && closingAction && (
				<ClosedReasonModal
					closeFunction={closeBondRequest}
					closeFunctionError={closeBondRequestError}
					isClosing={closeBondRequestLoading}
					onDismiss={() => {
						setShowClosedReasonModal(false);
						setClosingAction(null);
					}}
					closingAction={closingAction}
				/>
			)}
			{closeBondRequestLoading && (
				<Modal>
					<LoadingModal />
				</Modal>
			)}
		</BondRequestLayout>
	);
};
